import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createPaginationContainer, graphql } from 'react-relay';

import BadgeActive from 'src/components/Badge/BadgeActive';
import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import logger from 'src/util/logger';

/**
 * Description
 * @param {object} props
 * @param {object} props.relay
 * @param {object} props.viewer
 * @returns {React.ReactElement} - AdminTariffList component
 */
function AdminTariffList({ relay, viewer = null }) {
  const loadMore = () => {
    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    relay.loadMore(
      50,
      (error) => { logger.log(error); },
    );
  };

  const intl = useIntl();

  if (!viewer) {
    return <Loading />;
  }

  const { tariffs } = viewer;

  // Page title and breadcrumbs
  const pageTitle = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.page.title',
    defaultMessage: 'Enosi Admin - Tariffs',
  });
  const breadcrumbsAdmin = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.breadcrumbs.admin.label',
    defaultMessage: 'Admin',
  });
  const breadcrumbsTariffs = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.breadcrumbs.tariffs.label',
    defaultMessage: 'Tariffs',
  });

  // Table headers
  const statusHeader = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.status.label',
    defaultMessage: 'Status',
  });
  const titleHeader = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.title.label',
    defaultMessage: 'Title',
  });
  const identifierHeader = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.identifier.label',
    defaultMessage: 'Identifier',
  });
  const idHeader = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.id.label',
    defaultMessage: 'ID',
  });
  const externalIdHeader = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.external_identifier.title',
    defaultMessage: 'External Identifier',
  });
  const externalIdAbbrLabel = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.external_identifier.abbr.label',
    defaultMessage: 'Ext ID',
  });
  const tariffTypeHeader = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.tariff_type.label',
    defaultMessage: 'Tariff Type',
  });
  const meterHeader = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.meter.label',
    defaultMessage: 'Meter',
  });
  const propertyHeader = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.table.head.property.label',
    defaultMessage: 'Property',
  });

  // Load more button text
  const copyLoadMore = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  });
  const copyNoMore = intl.formatMessage({
    id: 'admin.admin_tariff.admin_tariff_list.load_more_button.state_no_more.label',
    defaultMessage: 'No more tariffs to load',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbsAdmin, path: '/admin' },
          { name: breadcrumbsTariffs },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">{breadcrumbsTariffs}</h1>
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{statusHeader}</th>
                <th scope="col">{titleHeader}</th>
                <th scope="col">{identifierHeader}</th>
                <th scope="col">{idHeader}</th>
                <th scope="col">
                  <abbr title={externalIdHeader}>{externalIdAbbrLabel}</abbr>
                </th>
                <th scope="col">{tariffTypeHeader}</th>
                <th scope="col">{meterHeader}</th>
                <th scope="col">{propertyHeader}</th>
              </tr>
            </thead>
            <tbody>
              {
                tariffs && tariffs.edges && tariffs.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    uuid, title, identifier, externalIdentifier, active,
                    tariffType, meter, property,
                  } = node;
                  const {
                    uuid: meterId, identifier: meterIdentifier, active: meterActive,
                  } = meter;
                  const {
                    uuid: propertyId, title: propertyTitle, active: propertyActive,
                  } = property;

                  return (
                    <tr key={`admin-tariff-${uuid}`}>
                      <td>
                        <BadgeActiveTimeRange active={active} />
                      </td>
                      <th scope="row" colSpan={2}>
                        <Link to={`/admin/tariffs/${uuid}`}>
                          {title}
                        </Link>
                        <br />
                        {identifier}
                      </th>
                      <td colSpan={2}>
                        <UUID uuid={uuid} />
                        <br />
                        {externalIdentifier}
                      </td>
                      <td>
                        {tariffType}
                      </td>
                      <td>
                        <Link to={`/admin/meters/${meterId}`}>
                          {meterIdentifier}
                        </Link>
                        <br />
                        <BadgeActive start={meterActive.start} finish={meterActive.finish} />
                      </td>
                      <td>
                        <Link to={`/admin/properties/${propertyId}`}>
                          {propertyTitle}
                        </Link>
                        <br />
                        <BadgeActive start={propertyActive.start} finish={propertyActive.finish} />
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={relay.hasMore()}
          />
        </div>
      </div>
    </>
  );
}

AdminTariffList.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.shape({
    id: PropTypes.string,
    viewerUser: PropTypes.shape({
      email: PropTypes.string,
      givenName: PropTypes.string,
      familyName: PropTypes.string,
    }),
    tariffs: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            uuid: PropTypes.string,
            externalIdentifier: PropTypes.string,
            identifier: PropTypes.string,
            title: PropTypes.string,
            tariffType: PropTypes.string,
            active: PropTypes.shape({
              start: PropTypes.number,
              finish: PropTypes.number,
            }),
            meter: PropTypes.shape({
              uuid: PropTypes.string,
              identifier: PropTypes.string,
              active: PropTypes.shape({
                start: PropTypes.number,
                finish: PropTypes.number,
              }),
            }),
            property: PropTypes.shape({
              uuid: PropTypes.string,
              title: PropTypes.string,
              active: PropTypes.shape({
                start: PropTypes.number,
                finish: PropTypes.number,
              }),
              address: PropTypes.shape({
                line1: PropTypes.string,
                line2: PropTypes.string,
                city: PropTypes.string,
                state: PropTypes.string,
                postcode: PropTypes.string,
              }),
            }),
          }),
        }),
      ),
    }),
  }),
};

export default createPaginationContainer(
  AdminTariffList,
  {
    viewer: graphql`
      fragment AdminTariffList_viewer on Viewer {
        id
        viewerUser {
          email
          givenName
          familyName
        }
        tariffs(
          first: $count
          after: $cursor
        ) @connection(key: "AdminTariffList_tariffs") {
          edges {
            cursor
            node {
              uuid
              externalIdentifier
              identifier
              title
              tariffType
              active {
                start
                finish
              }
              meter {
                uuid
                identifier
                active { start finish }
              }
              property {
                uuid
                title
                active { start finish }
              }
            }
          }
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.viewer.tariffs;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }) {
      return {
        count,
        cursor,
      };
    },
    query: graphql`
    # Pagination query to be fetched upon calling 'loadMore'.
    # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
    query AdminTariffList_Pagination_Query(
      $count: Int!
      $cursor: String
    ) {
      viewer {
        ...AdminTariffList_viewer
      }
    }
  `,
  },
);
